// src/components/games/GameAllGrid.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";

function getDiscountedPrice(price, rate) {
  return Math.round((price * (100 - rate)) / 100);
}

export default function GameAllGrid({
  sortField,
  sortOrder,
  searchTerm,
  priceRange = [0, Infinity],
  discountOnly,
  hideFree,
  excludeComingSoon,
  showOnlyComingSoon,
  filters,   // { gameJam: Set, genre: Set, rating: Set, language: Set }
  onCount
}) {
  const [items, setItems] = useState([]);
  const [bannerUrls, setBannerUrls] = useState({}); // { [gameId]: 다운로드된 HTTP URL }

  // 1) Firestore 'games' 구독
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "games"), snap => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, []);

  // 2) Storage에서 배너 이미지 URL 가져오기 + localStorage 캐싱
  useEffect(() => {
    const storage = getStorage();

    items.forEach(item => {
      // 이미 state에 준비된 URL이 있으면 무시
      if (bannerUrls[item.id]) return;

      // 2-1) Firestore에 bannerImageUrl 필드가 “HTTP URL”인지 체크
      const stored = item.bannerImageUrl || "";
      const isHttpUrl = stored.startsWith("http://") || stored.startsWith("https://");
      if (isHttpUrl) {
        // 이미 다운로드 가능한 URL이므로, 바로 캐시
        setBannerUrls(prev => ({ ...prev, [item.id]: stored }));
        try {
          localStorage.setItem(`gameBanner_${item.id}`, stored);
        } catch {}
        return;
      }

      // 2-2) localStorage에 캐시된 게 있으면 바로 사용
      const cached = localStorage.getItem(`gameBanner_${item.id}`);
      if (cached) {
        setBannerUrls(prev => ({ ...prev, [item.id]: cached }));
        return;
      }

      // 2-3) Firestore 값(stored)이 “스토리지 경로”인 경우 getDownloadURL 호출
      // 예: stored = "games/<id>/<id>_banner"
      // (stored가 빈 문자열이면, 아래에서 getDownloadURL이 실패할 수 있음 → catch 무시)
      const pathToFile = stored || `games/${item.id}/${item.id}_banner`;
      const ref = storageRef(storage, pathToFile);
      getDownloadURL(ref)
        .then(url => {
          setBannerUrls(prev => ({ ...prev, [item.id]: url }));
          try {
            localStorage.setItem(`gameBanner_${item.id}`, url);
          } catch {}
        })
        .catch(err => {
          // 파일이 없거나 권한 문제 등으로 실패할 수 있음
          console.warn(`Storage에서 banner URL 가져오기 실패 (${item.id}):`, err);
        });
    });
  }, [items, bannerUrls]);

  // 3) 필터링 & 4) Pill 필터
  let display = items
    .filter(item => {
      if (showOnlyComingSoon) return !!item.comingSoon;
      if (excludeComingSoon)   return !item.comingSoon;
      return true;
    })
    .filter(item => item.title.includes(searchTerm))
    .filter(item => {
      const p = Number(item.price) || 0;
      return p >= priceRange[0] && p <= priceRange[1];
    })
    .filter(item => (discountOnly ? (Number(item.discountRate) || 0) > 0 : true))
    .filter(item => (hideFree ? (Number(item.price) || 0) > 0 : true))
    .filter(item => {
      for (const [cat, set] of Object.entries(filters)) {
        if (set.size === 0) continue;
        if (cat === "gameJam") {
          const tags = Array.isArray(item.tags) ? item.tags : [];
          if (![...set].some(key => tags.some(t => t.includes(key)))) {
            return false;
          }
        } else if (cat === "genre") {
          const genres = Array.isArray(item.genres) ? item.genres.map(String) : [];
          if (![...set].some(key => genres.includes(key))) {
            return false;
          }
        } else if (cat === "language") {
          const langs = Array.isArray(item.languages) ? item.languages : [];
          const hasLang = [...set].some(key =>
            langs.some(langObj =>
              langObj.name === key && (langObj.audio || langObj.subtitle || langObj.ui)
            )
          );
          if (!hasLang) return false;
        } else {
          const value = String(item[cat] ?? "");
          if (!set.has(value)) return false;
        }
      }
      return true;
    });

  // 5) 정렬
  display.sort((a, b) => {
    let diff = 0;
    if (sortField === "title") {
      diff = a.title.localeCompare(b.title);
    } else if (sortField === "price") {
      diff = (Number(a.price) || 0) - (Number(b.price) || 0);
    }
    return sortOrder === "asc" ? diff : -diff;
  });

  // 6) 결과 개수 콜백
  useEffect(() => {
    onCount(display.length);
  }, [display, onCount]);

  return (
    <section className="container mx-auto px-6 py-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {display.map(item => {
          const bannerSrc = bannerUrls[item.id] || ""; // item.bannerImageUrl은 이제 state로 넣어두었으므로 bannerUrls에서만 꺼냄
          const price = Number(item.price) || 0;
          const rate  = Math.min(Math.max(Number(item.discountRate) || 0, 0), 100);
          const disc  = getDiscountedPrice(price, rate);

          // 배지 레이블과 색상 결정
          const badges = [];
          if (item.comingSoon) {
            badges.push({ label: "출시예정", color: "bg-purple-600" });
          }

          // 가격 표시
          let priceDisplay;
          if (!item.comingSoon && price === 0 && rate === 0) {
            priceDisplay = (
              <span className="text-sm font-bold text-green-500">무료</span>
            );
          } else if (item.comingSoon && price === 0 && rate === 0) {
            priceDisplay = (
              <span className="text-sm font-bold text-yellow-500">출시예정</span>
            );
          } else if (rate > 0) {
            priceDisplay = (
              <div className="flex items-baseline space-x-1 text-sm font-bold">
                <span className="text-red-600">-{rate.toLocaleString()}%</span>
                <span className="line-through text-gray-500">
                  ₩{price.toLocaleString()}
                </span>
                <span className="text-white">₩{disc.toLocaleString()}</span>
              </div>
            );
          } else {
            priceDisplay = (
              <span className="text-sm font-bold text-white">
                ₩{price.toLocaleString()}
              </span>
            );
          }

          return (
            <Link
              key={item.id}
              to={`/games/detail/${item.id}`}
              className="relative group bg-[#1f1f1f] rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition flex flex-col"
            >
              {/* badges */}
              {badges.length > 0 && (
                <div className="absolute top-3 left-3 flex space-x-2 z-10">
                  {badges.map((b, i) => (
                    <span
                      key={i}
                      className={`px-2 py-1 rounded-full text-sm font-semibold text-white ${b.color}`}
                    >
                      {b.label}
                    </span>
                  ))}
                </div>
              )}

              {/* 16:9 비율 배너 */}
              <div className="w-full aspect-banner bg-black overflow-hidden flex items-center justify-center">
                {bannerSrc ? (
                  <img
                    src={bannerSrc}
                    alt={item.title}
                    loading="lazy"
                    className="object-contain w-full h-full"
                  />
                ) : (
                  <span className="text-gray-500">이미지 없음</span>
                )}
              </div>

              {/* title / price */}
              <div className="p-4 bg-neutral-800 flex flex-col justify-between min-h-24">
                <h3 className="text-white font-bold text-base truncate mb-2">
                  {item.title}
                </h3>
                <div>{priceDisplay}</div>
              </div>
            </Link>
          );
        })}

        {display.length === 0 && (
          <p className="col-span-full text-center text-gray-400">게임이 없습니다.</p>
        )}
      </div>
    </section>
  );
}
